import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  X, History, RotateCcw, Eye, Upload, FileEdit, Tag, Trash2,
  ArrowRight, FilePlus, RefreshCcw,
} from 'lucide-react';


const ACTION_LABEL = {
  upload:     { ar: 'رَفع',         icon: FilePlus,   tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  replace:    { ar: 'استِبدال',     icon: RefreshCcw, tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  update:     { ar: 'تَعديل',       icon: FileEdit,   tone: 'bg-sky-100 text-sky-800 border-sky-200' },
  classify:   { ar: 'تَصنيف',       icon: FileEdit,   tone: 'bg-sky-100 text-sky-800 border-sky-200' },
  transition: { ar: 'انتِقال',      icon: ArrowRight, tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  tag:        { ar: 'وَسم',         icon: Tag,        tone: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
  delete:     { ar: 'حَذف',         icon: Trash2,     tone: 'bg-red-100 text-red-800 border-red-200' },
  archive:    { ar: 'أَرشَفَة',      icon: Trash2,     tone: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
  unarchive:  { ar: 'فَك أَرشَفَة',   icon: RotateCcw,  tone: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
  restore:    { ar: 'استِعادَة',     icon: RotateCcw,  tone: 'bg-amber-100 text-amber-800 border-amber-200' },
};


export default function DocVersionsDrawer({ doc, onClose, onMutated }) {
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.get(`/document-hub/${doc.id}/versions`);
      setVersions(data.items || []);
    } catch { toast.error('فَشَل تَحميل الإصدارات'); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (doc?.id) load(); /* eslint-disable-next-line */ }, [doc?.id]);

  const previewVersion = async (n) => {
    try {
      const r = await axiosInstance.get(
        `/document-hub/${doc.id}/versions/${n}/preview`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('هَذا الإصدار لا يَحتَوي على ملَف ثُنائي');
    }
  };

  const restore = async (n) => {
    const reason = window.prompt('سَبَب الاستِعادَة (اختياري):', '');
    if (reason === null) return;
    try {
      await axiosInstance.post(`/document-hub/${doc.id}/restore`,
        { version_no: n, reason: reason || undefined });
      toast.success(`تَم استِرجاع v${n}`);
      load(); onMutated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فَشَل'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="versions-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl h-full flex flex-col" dir="rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-base font-bold">سِجِل الإصدارات</div>
              <div className="text-xs text-slate-500 font-mono">{doc.title || doc.file_name}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="versions-close"
                  className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {busy ? (
            <div className="text-center text-slate-400 py-10 text-sm">…تَحميل</div>
          ) : versions.length === 0 ? (
            <div className="text-center text-slate-400 py-10 text-sm">لا تُوجَد إصدارات.</div>
          ) : (
            versions.map((v) => {
              const info = ACTION_LABEL[v.action] || ACTION_LABEL.update;
              const Icon = info.icon;
              return (
                <div key={v.version_no} className="rounded-lg border bg-white p-3"
                     data-testid={`doc-version-${v.version_no}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">
                        v{v.version_no}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 ${info.tone}`}>
                        <Icon className="w-3 h-3" /> {info.ar}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {v.file_path && (
                        <button onClick={() => previewVersion(v.version_no)}
                                data-testid={`doc-version-preview-${v.version_no}`}
                                title="مُعايَنَة هَذا الإصدار"
                                className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 inline-flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                        </button>
                      )}
                      {v.action !== 'delete' && (
                        <button onClick={() => restore(v.version_no)}
                                data-testid={`doc-version-restore-${v.version_no}`}
                                title="استِرجاع"
                                className="text-[10px] font-mono rounded px-1.5 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800 inline-flex items-center gap-1">
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">
                    {v.created_at?.slice(0, 16).replace('T', ' ')} ·
                    {' '}{v.user_name || v.user_id?.slice(0, 8)}
                  </div>
                  {v.reason && (
                    <div className="text-[11px] text-slate-700 mt-1 italic">“{v.reason}”</div>
                  )}
                  {v.snapshot?.file_size && (
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">
                      {v.snapshot.file_name} · {(v.snapshot.file_size / 1024).toFixed(1)} KB
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
